import { Tooltip } from 'antd';

import { libraryIconComponent } from '../common/LibraryIcon';
import { canonicalChoices, choiceLabelOf } from '../../utils/choiceValues';

/**
 * ChoiceChips — a Choice that allows multiple values, read: one chip per
 * entry, in the choices' order (ruling D2), each with its choice's colour dot
 * and icon (the author's own marks for it, as the single value carries them).
 * A wrapping row of `value-chip`s, the same READING chips a multi-date element
 * uses in the read view (docs/platform-standards.md §4): no hover state,
 * nothing to click.
 *
 * `max` caps how many chips show (a tile's 162px slot): the rest fold into a
 * "+N" chip whose tooltip lists every label. An entry whose choice was since
 * removed reads as it is stored, after the listed ones.
 */

function chipMeta(element, value) {
	const choices = Array.isArray(element?.options?.choices) ? element.options.choices : [];
	const found = choices.find(choice => choice !== null && typeof choice === 'object' && String(choice.value) === String(value));
	const Icon = found?.icon ? libraryIconComponent(found.icon) : null;
	return { color: found?.color || null, Icon };
}

export default function ChoiceChips({ element, value, max = null }) {
	const entries = canonicalChoices(element, value);
	if (!entries.length) return '—';
	const shown = Number.isInteger(max) && max > 0 ? entries.slice(0, max) : entries;
	const hidden = entries.slice(shown.length);
	return (
		<span className="choice-chips" data-testid="choice-chips">
			{shown.map((entry) => {
				const { color, Icon } = chipMeta(element, entry);
				return (
					<span className="value-chip choice-chip" key={entry}>
						{color ? <span className="choice-chip__dot" style={{ backgroundColor: color }} /> : null}
						{Icon ? <span className="choice-chip__icon"><Icon aria-hidden="true" /></span> : null}
						<span className="choice-chip__label">{choiceLabelOf(element, entry)}</span>
					</span>
				);
			})}
			{hidden.length ? (
				<Tooltip title={entries.map(entry => choiceLabelOf(element, entry)).join(', ')}>
					<span className="value-chip choice-chip choice-chip--more" data-testid="choice-chips-more">+{hidden.length}</span>
				</Tooltip>
			) : null}
		</span>
	);
}
